import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';

import { InvoicesCards } from './_authenticated._main.invoicing/InvoicesCards';
import { InvoicesTable } from './_authenticated._main.invoicing/InvoicesTable';
import { MonthlyInvoiceTotal } from './_authenticated._main.invoicing/MonthlyInvoiceTotal';
import { TurnoverByDriverTable } from './_authenticated._main.invoicing/TurnoverByDriverTable';
import { Invoice } from './_authenticated._main.invoicing/types';
import { SimplePagination } from '../components/SimplePagination';
import { prefetchInvoiceResumeByMonth, useFetchInvoiceResumeByMonth } from '../hooks/useFetchInvoiceResumeByMonth';
import { t } from '../services/translation/translation';
import {
  addMonths,
  endOfMonth,
  formatDateForParisTZ,
  isAfter,
  isBefore,
  parseDate,
  subtractMonths,
} from '../utils/date';

export const MonthlyInvoicingRoute = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParams = searchParams.get('selectedDate');
  const parsedDateParams = dateParams ? parseDate(dateParams, 'YYYY-MM-DD') : undefined;
  const initialDate =
    dateParams && parsedDateParams && isBefore(parsedDateParams, parseDate('2024-12-31', 'YYYY-MM-DD'))
      ? dateParams
      : '2024-12-01';
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const selectedDateNumber = parseDate(selectedDate, 'YYYY-MM-DD');
  const { data, isLoading, isFetching, isPreviousData } = useFetchInvoiceResumeByMonth(selectedDate);

  const invoices = useMemo<Invoice[]>(() => {
    if (!data) return [];
    return [
      ...data.invoiceIn.map(i => ({ ...i, type: 'in' as const })),
      ...data.invoiceOut.map(i => ({ ...i, type: 'out' as const })),
    ];
  }, [data]);

  const disabledNext =
    isBefore(Date.now(), endOfMonth(selectedDateNumber)) ||
    isAfter(addMonths(selectedDateNumber, 1), parseDate('2024-12-31', 'YYYY-MM-DD'));
  const previousDate = formatDateForParisTZ(subtractMonths(selectedDateNumber, 1), 'YYYY-MM-DD', false);
  const nextDate = formatDateForParisTZ(addMonths(selectedDateNumber, 1), 'YYYY-MM-DD', false);

  const handlePrevious = () => {
    if (!previousDate) return;
    setSelectedDate(previousDate);
  };

  const handleNext = () => {
    if (!nextDate) return;
    setSelectedDate(nextDate);
  };

  useEffect(() => {
    if (selectedDate) {
      setSearchParams(params => {
        params.set('selectedDate', selectedDate);
        return params;
      });
    }
  }, [selectedDate, setSearchParams]);

  useEffect(() => {
    if (previousDate) prefetchInvoiceResumeByMonth(previousDate);
    if (nextDate) prefetchInvoiceResumeByMonth(nextDate);
  }, [previousDate, nextDate]);

  return (
    <div>
      <Helmet>
        <title>{t('invoicing-route.title-month')} - Allocab</title>
      </Helmet>

      <h3 className="text-3xl font-bold dark:text-white mb-6">{t('invoicing-route.title-month')}</h3>

      <div className="space-y-4">
        <SimplePagination onNext={handleNext} onPrevious={handlePrevious} isNextDisabled={disabledNext}>
          <span className="capitalize">{formatDateForParisTZ(selectedDateNumber, t('date.month-year'), false)}</span>
        </SimplePagination>

        <MonthlyInvoiceTotal isFetching={isPreviousData && isFetching} data={data} />

        {data?.driversDetails ? (
          <TurnoverByDriverTable
            data={data.driversDetails}
            isLoading={isLoading}
            isFetching={isPreviousData && isFetching}
          />
        ) : null}
        <div className="hidden xl:block">
          <InvoicesTable invoices={invoices} isLoading={isLoading} isFetching={isPreviousData && isFetching} />
        </div>
        <div className="block xl:hidden">
          <InvoicesCards
            invoices={invoices}
            isLoading={isLoading}
            isFetching={isPreviousData && isFetching}
            title={t('common.invoices')}
          />
        </div>
      </div>
    </div>
  );
};
